import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, login } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });
  const [acceptAgb, setAcceptAgb] = useState(false);
  const [acceptDsgvo, setAcceptDsgvo] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptAgb || !acceptDsgvo) {
      toast.error('Bitte AGB und Datenschutzerklärung akzeptieren.');
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, role: 'customer' });
      await login(form.email, form.password);
      toast.success('Konto erstellt');
      navigate('/portal', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = acceptAgb && acceptDsgvo && !loading;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-md mx-auto px-4 sm:px-6 py-10">
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-6">
            <h1 className="font-heading font-semibold text-2xl text-[hsl(var(--primary))]">Konto erstellen</h1>
            <p className="text-sm text-slate-600 mt-1">Für Status-Tracking und Dokumentenupload.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Vorname</Label>
                  <Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} required className="h-12 rounded-xl mt-1" data-testid="register-first-name-input" />
                </div>
                <div>
                  <Label>Nachname</Label>
                  <Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} required className="h-12 rounded-xl mt-1" data-testid="register-last-name-input" />
                </div>
              </div>
              <div>
                <Label>E-Mail</Label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required className="h-12 rounded-xl mt-1" data-testid="register-email-input" />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+43 660 ..." className="h-12 rounded-xl mt-1" data-testid="register-phone-input" />
              </div>
              <div>
                <Label>Passwort</Label>
                <Input type="password" minLength={6} value={form.password} onChange={(e) => set('password', e.target.value)} required className="h-12 rounded-xl mt-1" data-testid="register-password-input" />
              </div>

              <div className="pt-2 space-y-3">
                <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer" data-testid="register-agb-row">
                  <Checkbox
                    checked={acceptAgb}
                    onCheckedChange={(v) => setAcceptAgb(Boolean(v))}
                    className="mt-1"
                    data-testid="register-agb-checkbox"
                  />
                  <span>
                    Ich akzeptiere die{' '}
                    <Link to="/impressum" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] underline" data-testid="register-agb-link">
                      Allgemeinen Geschäftsbedingungen
                    </Link>
                    .
                  </span>
                </label>
                <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer" data-testid="register-dsgvo-row">
                  <Checkbox
                    checked={acceptDsgvo}
                    onCheckedChange={(v) => setAcceptDsgvo(Boolean(v))}
                    className="mt-1"
                    data-testid="register-dsgvo-checkbox"
                  />
                  <span>
                    Ich habe die{' '}
                    <Link to="/datenschutz" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] underline" data-testid="register-dsgvo-link">
                      Datenschutzerklärung
                    </Link>{' '}
                    gelesen und stimme der Verarbeitung meiner Daten zu.
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={!canSubmit}
                className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit-button"
              >
                {loading ? 'Speichern...' : 'Konto erstellen'}
              </Button>
            </form>
            <div className="mt-4 text-sm text-slate-600">
              Schon registriert? <Link to="/login" className="text-[hsl(var(--primary))] font-medium">Anmelden</Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
